// Placement-time checks (WAIVERS.md: "A claim that cannot succeed as
// placed is refused with the reason, not accepted and failed later"). The
// same rules run again at processing; these give the reason up front.

import { noRunLeftSentence } from './copy';
import { limitHit, limitSentence } from './engine';
import { rosterCapacity } from '@fsp/domain';
import { activeOf, dropLeavesProblem, fitProblem } from './fit';
import type {
  AcquisitionCount,
  AcquisitionLimits,
  Claim,
  WaiverContext,
  WaiverRules,
} from './types';

export interface MoveInput {
  rules: WaiverRules;
  limits: AcquisitionLimits;
  teamId: string;
  /** The team's roster right now. */
  roster: readonly string[];
  /** Players whose game has started (game-time lock applies to adds and drops). */
  locked: ReadonlySet<string>;
  acquisitionsUsed: AcquisitionCount | undefined;
}

export interface ClaimInput extends MoveInput {
  playerId: string;
  bid: number | null;
  dropPlayerIds: readonly string[];
  conditionalOnClaimId: string | null;
  /** FAAB left right now: the whole of it is what a bid is checked against. */
  budget: number;
  /** The team's other pending claims, for duplicates and conditionals. They hold back no budget. */
  myClaims: readonly Claim[];
  /** The claim being edited, if any; it is not a duplicate of itself. */
  editingClaimId: string | null;
  /** Whether the player is actually on waivers right now. */
  onWaivers: boolean;
  /**
   * When this claim will be decided: the run at or after the player clears
   * (on a continuous schedule, the moment he clears). What a drop in it is
   * judged at, since that is when it happens.
   */
  decidedAt: number;
  /** When the game-time lock lifts: the end of the period being played. */
  lockEndsAt: number;
  /**
   * The last instant a run can decide anything this season: the end of its
   * last period, when the season rolls over and moves close (B-152). A claim
   * decided after it would never be decided, and is refused. Absent or null
   * for a host whose runs never stop — the simulator's demo keeps running
   * waivers past its bracket.
   */
  lastRunBy?: number | null;
}

/**
 * Whether the game-time lock still holds a claim's drop when the claim is
 * decided at `decidedAt`: a drop whose game has started cannot be made until
 * the period ends (PLAN-2026-09-24 §2 rule 6), and a claim's drop happens at
 * its run, not when it is placed.
 *
 * The default Wednesday run comes after the period, so there a started player
 * of mine can be a claim's drop. A daily run (or a weekly one on a day the
 * period is still being played) can come inside it, and a continuous claim is
 * decided the moment its player clears, so there he cannot. This was "only a continuous schedule" until the W3 review found a
 * daily run inside the period dropping a started player: the placement check,
 * the claim sheet and the run all read this now, and the run checks the drops
 * against the players locked at its own instant.
 */
export function dropLockHolds(rules: WaiverRules, decidedAt: number, lockEndsAt: number): boolean {
  return rules.gameTimeLock && decidedAt < lockEndsAt;
}

/** Why an add would be refused, or null. */
export function addProblem(
  input: MoveInput & { playerId: string; dropPlayerIds: readonly string[] },
  ctx: WaiverContext,
): string | null {
  if (input.roster.includes(input.playerId)) return 'Already on your roster.';
  // In a league with scheduled runs a free agent whose game has started is on
  // waivers until the first run after the lock lifts (`clearsAtAfterKickoff`,
  // PLAN-2026-09-24 §2 rule 6), and the host refuses the add as "on waivers"
  // before it gets here. This sentence is for the leagues where nothing puts
  // the player on waivers in the meantime: no waivers, or a continuous
  // schedule.
  if (input.rules.gameTimeLock && input.locked.has(input.playerId))
    return `${ctx.playerName(input.playerId)} is locked until the week ends.`;
  const missing = input.dropPlayerIds.find((id) => !input.roster.includes(id));
  if (missing) return `${ctx.playerName(missing)} is not on your roster.`;
  const lockedDrop = input.rules.gameTimeLock
    ? input.dropPlayerIds.find((id) => input.locked.has(id))
    : undefined;
  if (lockedDrop) return `${ctx.playerName(lockedDrop)} is locked until the week ends.`;
  const limit = limitHit(input.limits, input.acquisitionsUsed, ctx.positionOf(input.playerId));
  if (limit) return `You have used ${limitSentence(limit)}.`;
  const after = [...input.roster.filter((id) => !input.dropPlayerIds.includes(id)), input.playerId];
  return fitProblem(input.teamId, after, ctx);
}

/** Why a drop would be refused, or null. */
export function dropProblem(
  input: MoveInput & { playerId: string },
  ctx: WaiverContext,
): string | null {
  if (!input.roster.includes(input.playerId)) return 'Not on your roster.';
  if (input.rules.gameTimeLock && input.locked.has(input.playerId))
    return `${ctx.playerName(input.playerId)} is locked until the week ends.`;
  return dropLeavesProblem(
    input.teamId,
    input.roster.filter((id) => id !== input.playerId),
    ctx,
  );
}

/**
 * Whether any single drop makes room for this player, when the roster is
 * full. The Add button is disabled with the reason when nothing would.
 */
export function canMakeRoom(
  input: MoveInput & { playerId: string },
  ctx: WaiverContext,
): { ok: true; needsDrop: boolean } | { ok: false; reason: string } {
  const direct = addProblem({ ...input, dropPlayerIds: [] }, ctx);
  if (!direct) return { ok: true, needsDrop: false };
  // His own player is his already; no drop changes that.
  if (input.roster.includes(input.playerId)) return { ok: false, reason: direct };
  // FULL BY COUNT, OR BY LAYOUT: a full bench with an open starting slot the
  // player cannot play is room only a drop can make (`fits`).
  const active = activeOf(input.teamId, input.roster, ctx);
  const full =
    active.length >= rosterCapacity(ctx.roster) ||
    (ctx.fits?.(input.teamId, [...active, input.playerId]) ?? null) !== null ||
    // A PLAYER ON IR WHO HAS TO COME OFF (D9): only dropping him makes room,
    // which the loop below finds.
    (ctx.reserveHold?.(input.teamId, input.roster) ?? null) !== null;
  if (!full) return { ok: false, reason: direct };
  const lockedMsg = input.rules.gameTimeLock && input.locked.has(input.playerId);
  if (lockedMsg) return { ok: false, reason: direct };
  const limit = limitHit(input.limits, input.acquisitionsUsed, ctx.positionOf(input.playerId));
  if (limit) return { ok: false, reason: direct };
  for (const id of input.roster) {
    if (!addProblem({ ...input, dropPlayerIds: [id] }, ctx)) return { ok: true, needsDrop: true };
  }
  // A PLAYER ON IR WHO HAS TO COME OFF, and no drop clears it (he is locked):
  // that is the reason, not a full roster (the review of D9).
  if ((ctx.reserveHold?.(input.teamId, input.roster) ?? null) !== null)
    return { ok: false, reason: direct };
  // EVERYONE LOCKED: Monday night after every game, or all week in a league
  // whose lineups lock whole at the first game (B-148). Said as what it is;
  // "no drop would make this roster legal" sent people looking at positions.
  if (input.rules.gameTimeLock && input.roster.every((id) => input.locked.has(id)))
    return {
      ok: false,
      reason: 'Roster full, and everyone on it is locked until the week ends.',
    };
  // WHICH POSITION TO NAME, and the old answer was wrong twice over. It read
  // `length <= 1` over every dedicated starter slot, which assumed each one
  // needs exactly one player — a two-running-back league was told nothing —
  // and, worse, it fired for a roster holding NONE of the position. A team
  // with no quarterback at all was told "every drop would leave you short a
  // QB", which is a sentence about a drop that has not happened, addressed to
  // somebody who is already short (AUDIT-2026-09-19 F26).
  //
  // The position worth naming is one sitting EXACTLY on its minimum, because
  // that is the one a drop breaks. Below the minimum is already broken and
  // above it has room. And none of this applies at all when the league allows
  // an empty slot, because then no drop can make a roster illegal by leaving
  // a slot bare — the refusal above is about something else and a sentence
  // about starters would send somebody looking in the wrong place.
  const needed = new Map<string, number>();
  if (!ctx.roster.allowEmptySlots) {
    for (const slot of ctx.roster.slots) {
      if (slot.kind !== 'starter' || slot.eligible.length !== 1) continue;
      const only = slot.eligible[0]!;
      needed.set(only, (needed.get(only) ?? 0) + slot.count);
    }
  }
  const pos = [...needed].find(([only, need]) => {
    if (need <= 0) return false;
    return input.roster.filter((id) => ctx.positionOf(id) === only).length === need;
  })?.[0];
  return {
    ok: false,
    reason: pos
      ? `Roster full and every drop would leave you short a ${pos}.`
      : 'Roster full and no drop would make this roster legal.',
  };
}

/** Why a claim would be refused as placed, or null. */
export function claimProblem(input: ClaimInput, ctx: WaiverContext): string | null {
  const { rules } = input;
  if (rules.system === 'none') return 'This league has no waivers. Add players directly.';
  if (!input.onWaivers) return `${ctx.playerName(input.playerId)} is not on waivers.`;
  if (input.roster.includes(input.playerId)) return 'Already on your roster.';
  // NO RUN LEFT: the claim would wait for a run after the season closes, and
  // be promised a time that never comes (the review of W7, 2026-09-24).
  if (input.lastRunBy != null && input.decidedAt > input.lastRunBy)
    return noRunLeftSentence(ctx.playerName(input.playerId));
  const duplicate = input.myClaims.find(
    (c) => c.status === 'pending' && c.playerId === input.playerId && c.id !== input.editingClaimId,
  );
  if (duplicate) return `You already have a claim on ${ctx.playerName(input.playerId)}.`;
  if (rules.system === 'faab') {
    const faab = rules.faab ?? {
      budget: 0,
      minBid: 0,
      allowZeroBids: true,
      tiebreak: 'waiver-priority' as const,
    };
    const bid = input.bid ?? 0;
    if (!Number.isInteger(bid) || bid < 0) return 'Bids are whole dollars.';
    if (bid === 0 && !faab.allowZeroBids) return 'This league does not allow $0 bids.';
    if (bid < faab.minBid) return `The minimum bid is $${faab.minBid}.`;
    // THE WHOLE BUDGET, NOT WHAT IS LEFT AFTER THE OTHER CLAIMS
    // (PLAN-2026-09-24 §2 rule 5). A pending claim holds back nothing: two
    // big bids on two players, knowing only one can win, is the common move
    // on every platform the research read. The run spends in the team's own
    // order, and a claim whose bid is more than what is left by then loses as
    // `over-budget` with the amount. This summed every other pending bid until
    // W3, so a backup claim was refused over money it could never spend.
    if (bid > input.budget) return `Bid $${bid} with $${input.budget} left.`;
  }
  const missing = input.dropPlayerIds.find((id) => !input.roster.includes(id));
  if (missing) return `${ctx.playerName(missing)} is not on your roster.`;
  const lockedDrop = dropLockHolds(rules, input.decidedAt, input.lockEndsAt)
    ? input.dropPlayerIds.find((id) => input.locked.has(id))
    : undefined;
  if (lockedDrop)
    return `${ctx.playerName(lockedDrop)} is locked until the week ends, and this claim is decided before then.`;
  const limit = limitHit(input.limits, input.acquisitionsUsed, ctx.positionOf(input.playerId));
  if (limit) return `You have used ${limitSentence(limit)}.`;
  if (input.conditionalOnClaimId) {
    if (!rules.conditionalClaims) return 'This league does not allow conditional claims.';
    const parent = input.myClaims.find(
      (c) => c.id === input.conditionalOnClaimId && c.status === 'pending',
    );
    if (!parent) return 'That claim is no longer pending.';
    if (parent.id === input.editingClaimId) return 'A claim cannot depend on itself.';
  }
  const after = [...input.roster.filter((id) => !input.dropPlayerIds.includes(id)), input.playerId];
  return fitProblem(input.teamId, after, ctx);
}
